import type { Node as ProseMirrorNode } from '@tiptap/pm/model';
import {
  getDialogueStatsInRange,
  getSceneDialogueStats,
  isSceneHeadingParagraphText,
  type SceneDialogueStats,
} from '@narrativebench/editor-core';
import { getSectionRange } from './document-section';
import { formatSceneDialogueStatLabels } from './script-scene-stats';

export const EXPORT_SCRIPT_STATS_SEPARATOR = ' · ';
export const EXPORT_SCRIPT_STATS_FONT_SIZE_PT = 9;
export const EXPORT_SCRIPT_STATS_GREY = '808080';
export const EXPORT_SCRIPT_STATS_PDF_GREY_RGB: [number, number, number] = [128, 128, 128];

export function formatExportScriptStatsLine(
  stats: SceneDialogueStats & { sceneCount?: number },
  t: (key: string) => string,
): string {
  const labels = formatSceneDialogueStatLabels(stats, t);
  return [labels.scenes, labels.dialogWords, labels.dialogEstimation]
    .filter((part): part is string => Boolean(part))
    .join(EXPORT_SCRIPT_STATS_SEPARATOR);
}

export function collectExportScriptStatsQueue(
  doc: ProseMirrorNode,
  t: (key: string) => string,
): Array<string | null> {
  const queue: Array<string | null> = [];
  doc.descendants((node, pos) => {
    if (node.type.name === 'footnotes') {
      return false;
    }
    if (node.type.name === 'heading') {
      const range = getSectionRange(doc, pos);
      if (!range) {
        queue.push(null);
        return true;
      }
      const stats = getDialogueStatsInRange(doc, range.from, range.to);
      queue.push(stats ? formatExportScriptStatsLine(stats, t) : null);
      return true;
    }
    if (
      node.type.name === 'paragraph'
      && isSceneHeadingParagraphText(node.textContent)
    ) {
      const stats = getSceneDialogueStats(doc, pos);
      queue.push(stats ? formatExportScriptStatsLine(stats, t) : null);
    }
    return true;
  });
  return queue;
}

export function takeNextExportScriptStatsLine(
  queue: Array<string | null>,
): string | null {
  if (queue.length === 0) {
    return null;
  }
  return queue.shift() ?? null;
}
